import {
  type V2AdminReservationDto,
  v2AdminReservationsControllerAuthorize,
  v2AdminReservationsControllerCreate,
  v2AdminReservationsControllerDecline,
  v2AdminReservationsControllerRelease
} from '@idc-forms/api-client'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Comandos de reserva contra la API v2.
 *
 * El inmueble viaja como `prp_` y la reserva como `rsv_`. Dos diferencias de forma con v1 que se
 * notan al llamar:
 *
 * - **`endsAt` en vez de `reservedDates`.** La retención es una ventana con un final exacto, no una
 *   lista de días. Quien llama manda el instante en que la reserva debe morir.
 * - **No existe `resendAuthorization`.** Las capabilities de v2 se acuñan una vez y vencen; volver
 *   a mandar el mismo link no es reintentar, es alargar un permiso por la puerta de atrás. Mientras
 *   ese comando no se rediseñe, el reenvío sigue siendo de v1.
 */
export type ReservationV2 = V2AdminReservationDto

export const reservationsV2Service = {
  create: async (
    accessToken: string,
    input: { property: string; endsAt: string; notes?: string },
    idempotencyKey?: string
  ): Promise<ReservationV2> => {
    const result = await v2AdminReservationsControllerCreate({
      ...v2RequestOptions,
      headers: {
        ...bearer(accessToken),
        // Sin la cabecera el servidor deja pasar y avisa; con ella, el reintento replica la reserva
        // original en vez de chocar contra el índice único.
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {})
      },
      body: input as never
    })
    return result.data
  },

  release: async (accessToken: string, id: string, notes?: string): Promise<ReservationV2> => {
    const result = await v2AdminReservationsControllerRelease({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { notes } as never
    })
    return result.data
  },

  authorize: async (accessToken: string, id: string, notes?: string): Promise<ReservationV2> => {
    const result = await v2AdminReservationsControllerAuthorize({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { notes } as never
    })
    return result.data
  },

  decline: async (accessToken: string, id: string, notes?: string): Promise<ReservationV2> => {
    const result = await v2AdminReservationsControllerDecline({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { notes } as never
    })
    return result.data
  }
}
